import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, LayoutGrid, List, X } from "lucide-react";
import api from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import PropertyCardSkeleton from "@/components/PropertyCardSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { priceLabel } from "@/lib/format";
import { Link } from "react-router-dom";

const TYPES = ["House", "Flat", "Apartment", "Bungalow", "Cottage", "New Build", "Commercial", "Land"];

export default function Properties({ listing, featuredOnly }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [view, setView] = useState("grid");
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    property_type: params.get("property_type") || "any",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    min_beds: params.get("min_beds") || "any",
    min_baths: params.get("min_baths") || "any",
    furnished: "any",
    sort: "newest",
  });

  const load = useCallback(async (p = 1) => {
    setData(null);
    const q = new URLSearchParams();
    if (listing) q.set("listing_type", listing);
    if (featuredOnly) q.set("featured", "true");
    if (filters.q) q.set("q", filters.q);
    if (filters.property_type !== "any") q.set("property_type", filters.property_type);
    if (filters.min_price) q.set("min_price", filters.min_price);
    if (filters.max_price) q.set("max_price", filters.max_price);
    if (filters.min_beds !== "any") q.set("min_beds", filters.min_beds);
    if (filters.min_baths !== "any") q.set("min_baths", filters.min_baths);
    if (filters.furnished !== "any") q.set("furnished", filters.furnished);
    q.set("sort", filters.sort);
    q.set("page", p);
    q.set("per_page", "9");
    try {
      const res = await api.get(`/properties?${q.toString()}`);
      setData(res.data);
      setPage(p);
    } catch {
      setData({ items: [], total: 0, pages: 0 });
    }
  }, [filters, listing, featuredOnly]);

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [filters.sort, listing, featuredOnly]);
  useEffect(() => {
    const t = setTimeout(() => load(1), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters.q, filters.property_type, filters.min_price, filters.max_price, filters.min_beds, filters.min_baths, filters.furnished]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clear = () => {
    setFilters({ q: "", property_type: "any", min_price: "", max_price: "", min_beds: "any", min_baths: "any", furnished: "any", sort: "newest" });
    setParams({});
  };

  const title = featuredOnly ? "Featured Properties" : listing === "sale" ? "Properties For Sale" : listing === "let" ? "Properties To Let" : "All Properties";

  const FilterFields = () => (
    <div className="space-y-4">
      <div className="space-y-1.5"><Label>Location or postcode</Label><Input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="e.g. London, EH3" data-testid="filter-location" /></div>
      <div className="space-y-1.5"><Label>Property type</Label>
        <Select value={filters.property_type} onValueChange={(v) => set("property_type", v)}>
          <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="any">Any type</SelectItem>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Min price</Label><Input type="number" value={filters.min_price} onChange={(e) => set("min_price", e.target.value)} placeholder="£" data-testid="filter-min-price" /></div>
        <div className="space-y-1.5"><Label>Max price</Label><Input type="number" value={filters.max_price} onChange={(e) => set("max_price", e.target.value)} placeholder="£" data-testid="filter-max-price" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Min beds</Label>
          <Select value={filters.min_beds} onValueChange={(v) => set("min_beds", v)}>
            <SelectTrigger data-testid="filter-beds"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="any">Any</SelectItem>{[1,2,3,4,5].map((b) => <SelectItem key={b} value={String(b)}>{b}+</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Min baths</Label>
          <Select value={filters.min_baths} onValueChange={(v) => set("min_baths", v)}>
            <SelectTrigger data-testid="filter-baths"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="any">Any</SelectItem>{[1,2,3].map((b) => <SelectItem key={b} value={String(b)}>{b}+</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {listing === "let" && (
        <div className="space-y-1.5"><Label>Furnished</Label>
          <Select value={filters.furnished} onValueChange={(v) => set("furnished", v)}>
            <SelectTrigger data-testid="filter-furnished"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="Furnished">Furnished</SelectItem><SelectItem value="Unfurnished">Unfurnished</SelectItem></SelectContent>
          </Select>
        </div>
      )}
      <Button variant="outline" className="w-full rounded-full gap-2" onClick={clear} data-testid="clear-filters"><X className="w-4 h-4" /> Clear filters</Button>
    </div>
  );

  return (
    <div>
      <div className="bg-[#111827] py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-bold text-[#C5A059] uppercase tracking-[0.15em]">Property search</p>
          <h1 className="mt-2 font-display text-4xl font-bold text-white tracking-tight">{title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 bg-white rounded-2xl border border-border p-6">
            <h3 className="font-semibold text-[#111827] mb-4 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Filters</h3>
            <FilterFields />
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <p className="text-sm text-muted-foreground" data-testid="results-count">
              {data ? `${data.total} propert${data.total === 1 ? "y" : "ies"} found` : "Loading..."}
            </p>
            <div className="flex items-center gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="lg:hidden rounded-full gap-2" data-testid="mobile-filter-btn"><SlidersHorizontal className="w-4 h-4" /> Filters</Button>
                </SheetTrigger>
                <SheetContent side="left" className="overflow-y-auto">
                  <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
                  <div className="mt-6"><FilterFields /></div>
                </SheetContent>
              </Sheet>
              <Select value={filters.sort} onValueChange={(v) => set("sort", v)}>
                <SelectTrigger className="w-[180px] h-9" data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="price_asc">Price: low to high</SelectItem>
                  <SelectItem value="price_desc">Price: high to low</SelectItem>
                  <SelectItem value="beds_desc">Most bedrooms</SelectItem>
                  <SelectItem value="updated">Recently updated</SelectItem>
                </SelectContent>
              </Select>
              <div className="hidden sm:flex border border-border rounded-full p-0.5">
                <button onClick={() => setView("grid")} className={`p-1.5 rounded-full ${view === "grid" ? "bg-[#111827] text-white" : "text-muted-foreground"}`} data-testid="view-grid"><LayoutGrid className="w-4 h-4" /></button>
                <button onClick={() => setView("list")} className={`p-1.5 rounded-full ${view === "list" ? "bg-[#111827] text-white" : "text-muted-foreground"}`} data-testid="view-list"><List className="w-4 h-4" /></button>
              </div>
            </div>
          </div>

          {data === null ? (
            <div className={view === "grid" ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" : "space-y-4"}>
              {Array.from({ length: 6 }).map((_, i) => <PropertyCardSkeleton key={i} />)}
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-border" data-testid="empty-state">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto"><SlidersHorizontal className="w-7 h-7 text-muted-foreground" /></div>
              <h3 className="mt-4 font-display text-xl font-bold text-[#111827]">No properties found</h3>
              <p className="mt-2 text-muted-foreground">Try adjusting or clearing your filters.</p>
              <Button onClick={clear} className="mt-5 rounded-full bg-[#C81D35] hover:bg-[#a5162b]">Clear filters</Button>
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {data.items.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          ) : (
            <div className="space-y-4">
              {data.items.map((p) => (
                <Link key={p.id} to={`/property/${p.slug}`} className="flex gap-5 bg-white rounded-2xl border border-border overflow-hidden hover:shadow-md transition-shadow" data-testid={`list-item-${p.id}`}>
                  <div className="w-48 shrink-0 aspect-[4/3] bg-muted">{p.images?.[0]?.url && <img src={p.images[0].url} alt={p.title} loading="lazy" className="w-full h-full object-cover" />}</div>
                  <div className="py-4 pr-5 flex-1">
                    <p className="text-lg font-extrabold tracking-tight text-[#C81D35] font-body">{priceLabel(p)}</p>
                    <h3 className="font-body font-bold text-[#111827]">{p.title}</h3>
                    <p className="text-sm text-muted-foreground">{[p.area, p.town].filter(Boolean).join(", ")}</p>
                    {p.summary && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.summary}</p>}
                  </div>
                </Link>
              ))}
            </div>
          )}

          {data && data.pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2" data-testid="pagination">
              {Array.from({ length: data.pages }).map((_, i) => (
                <button key={i} onClick={() => { load(i + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className={`w-10 h-10 rounded-full text-sm font-semibold ${page === i + 1 ? "bg-[#C81D35] text-white" : "bg-white border border-border text-[#111827] hover:border-[#C81D35]"}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
